import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const verticals = [
  {
    n: "01",
    title: "Brand strategy & identity",
    intro:
      "For brands that need to get clear on who they are, what they stand for, and how they show up — before spending another rupee on content or campaigns.",
    tiers: [
      {
        name: "Clarity Sprint",
        tag: "Brand audit",
        deliverables: [
          "Brand audit & gap analysis",
          "Positioning statement",
          "Voice & tone guidelines",
          "Competitor landscape snapshot",
          "Recommendations deck",
        ],
        bestFor: "brands that need a diagnosis before a prescription",
      },
      {
        name: "Brand Foundation",
        tag: "Most requested",
        featured: true,
        deliverables: [
          "Full brand strategy document",
          "Positioning & narrative framework",
          "Voice, tone & messaging guide",
          "Visual identity direction",
          "Platform-specific guidelines",
          "2 rounds of revision",
        ],
        bestFor: "early-stage startups & brands in transition",
      },
      {
        name: "Brand Overhaul",
        tag: "Full overhaul",
        deliverables: [
          "Everything in Brand Foundation",
          "Full visual system rebuild",
          "GTM narrative & launch strategy",
          "Stakeholder alignment support",
          "Campaign-ready exit brief",
        ],
        bestFor: "funded brands, rebrands, and pre-launch GTM",
      },
    ],
  },
  {
    n: "02",
    title: "Campaign strategy & influencer marketing",
    intro:
      "End-to-end campaign leadership for brands that want culturally resonant, results-driven work — not just content calendars.",
    tiers: [
      {
        name: "Campaign Sprint",
        tag: "Single campaign",
        deliverables: [
          "Campaign concept & brief",
          "Platform strategy",
          "Influencer shortlist & outreach",
          "Content direction & scripts",
          "Launch execution support",
        ],
        bestFor: "product launches, seasonal campaigns, single activations",
      },
      {
        name: "Full Campaign",
        tag: "Most requested",
        featured: true,
        deliverables: [
          "Insight-led campaign strategy",
          "Creative concept development",
          "End-to-end influencer management",
          "Cross-platform amplification",
          "Shoot coordination support",
          "Post-campaign analysis",
        ],
        bestFor: "funded brands running awareness or launch campaigns",
      },
      {
        name: "Strategic Lead",
        tag: "Full ownership",
        deliverables: [
          "Everything in Full Campaign",
          "Stakeholder & budget management",
          "PR & media coordination",
          "Crisis communication readiness",
          "ORM setup for the campaign window",
        ],
        bestFor:
          "high-stakes launches requiring brand leadership, not just execution",
      },
    ],
    note: "Fi × Sima Taparia — 2M+ views, delivered in one week — is an example of Strategic Lead work.",
  },
  {
    n: "03",
    title: "Social media & content management",
    intro:
      "Ongoing social presence for brands that want consistency, quality, and real strategic thinking — not just someone posting on schedule.",
    tiers: [
      {
        name: "Presence",
        tag: "Getting started",
        deliverables: [
          "1 platform managed",
          "12 posts per month",
          "Content calendar",
          "Community management",
          "Monthly performance summary",
        ],
        bestFor: "small businesses building a consistent presence",
      },
      {
        name: "Growth",
        tag: "Most requested",
        featured: true,
        deliverables: [
          "2 platforms managed",
          "20 posts per month",
          "Strategy + content creation",
          "Active community management",
          "Monthly strategy review call",
          "ORM monitoring",
        ],
        bestFor: "D2C brands and funded startups scaling social",
      },
      {
        name: "Brand Voice",
        tag: "Full ownership",
        deliverables: [
          "3 platforms managed",
          "30+ posts per month",
          "Full content strategy ownership",
          "Crisis response readiness",
          "Influencer integration",
          "Weekly check-ins",
        ],
        bestFor: "brands that want strategic ownership, not just management",
      },
    ],
    note: "Retainer engagements are a minimum of 3 months. Includes AI-assisted creative production where relevant.",
  },
  {
    n: "04",
    title: "Content writing & SEO",
    intro:
      "For brands that need words that work — not just filler content. Blog writing, website copy, LinkedIn thought leadership, newsletters, and full content systems — all written with strategy behind them.",
    tiers: [
      {
        name: "Content Piece",
        tag: "One-off",
        deliverables: [
          "Single blog, article, or web page",
          "SEO keyword integration",
          "Brand voice alignment",
          "1 round of revision",
        ],
        bestFor: "brands that need one strong piece done right",
      },
      {
        name: "Content Package",
        tag: "Most requested",
        featured: true,
        deliverables: [
          "4–8 pieces per month",
          "SEO strategy & keyword planning",
          "Blog, LinkedIn & email writing",
          "Brand voice & tone consistency",
          "Monthly content calendar",
          "Performance recommendations",
        ],
        bestFor: "startups building content presence and organic reach",
      },
      {
        name: "Content System",
        tag: "Full system",
        deliverables: [
          "Everything in Content Package",
          "Blog system architecture & setup",
          "SEO audit & content roadmap",
          "Website copy overhaul",
          "Founder LinkedIn strategy & writing",
          "Email sequence setup",
        ],
        bestFor: "brands building content as a long-term growth channel",
      },
    ],
  },
];

const additional = [
  ["AI product photography", "Studio-quality campaign imagery from raw product shots, at a fraction of the time and cost."],
  ["Amazon storefront setup", "Full store build: listings, A+ content, keyword strategy, brand page, and AI visuals."],
  ["Amazon store management", "Ongoing listing optimisation, performance monitoring, and content updates."],
  ["ORM system setup", "Frameworks, tooling, escalation matrices, and team training for scalable reputation management."],
  ["Newsletter strategy & setup", "End-to-end newsletter IP: concept, platform selection, content framework, and first issues."],
  ["SEO blog writing", "Individual SEO-optimised articles, keyword-led and brand-consistent."],
  ["Website copywriting", "Page-by-page website copy that converts."],
  ["Brand workshop", "A focused half-day session for founders and teams to align on positioning, voice, and direction."],
];

export default function Services() {
  usePageMeta({
    title: "Services — Shivani | Brand Strategy & Social Media",
    description: "Brand strategy, identity, social media management, and campaign execution. Custom-scoped for founders, D2C brands, and agencies.",
    ogTitle: "Services — Shivani | Brand Strategy & Social Media",
    ogDescription: "Custom brand strategy, identity, and social media — scoped to what you actually need.",
  });
  return (
    <div data-testid="services-page">
      {/* PAGE INTRO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-16 md:pt-24 pb-10">
        <p className="eyebrow mb-8">Services</p>
        <h1
          className="serif-display leading-[0.9]"
          style={{ fontSize: "clamp(3rem, 8vw, 7rem)" }}
        >
          Services.
        </h1>
        <p
          className="mt-10 text-base md:text-lg leading-relaxed max-w-[68ch]"
          data-testid="services-intro"
        >
          I don&apos;t just make things, I make them mean something. Every engagement starts
          with understanding what your brand is actually trying to say, and who needs to hear it.
          Then we build the strategy, the creative, and the systems to say it, consistently,
          across every channel that matters.
        </p>
      </section>

      {/* VERTICALS */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pb-16">
        {verticals.map((v) => (
          <div
            key={v.n}
            data-testid={`services-vertical-${v.n}`}
            className="py-16 md:py-24"
            style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
          >
            {/* Vertical header */}
            <div className="grid md:grid-cols-12 gap-10 mb-14 md:mb-20">
              <div className="md:col-span-4 flex items-start gap-6">
                <span
                  className="serif-display text-5xl md:text-6xl leading-none"
                  style={{ color: "rgba(26,26,26,0.4)" }}
                >
                  {v.n}
                </span>
                <h2 className="serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.02]">
                  {v.title}
                </h2>
              </div>
              <p
                className="md:col-span-7 md:col-start-6 md:pt-4 text-base md:text-lg leading-relaxed max-w-[60ch]"
                style={{ color: "rgba(26,26,26,0.78)" }}
              >
                {v.intro}
              </p>
            </div>

            {/* 3-tier grid */}
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-6">
              {v.tiers.map((t, i) => (
                <li
                  key={t.name}
                  data-testid={`tier-${v.n}-${i + 1}`}
                  className="p-8 md:p-10 flex flex-col"
                  style={{
                    backgroundColor: t.featured ? "#E8E2D5" : "transparent",
                    border: "0.5px solid rgba(26,26,26,0.45)",
                    marginTop: i > 0 ? "-0.5px" : "0",
                  }}
                >
                  <div className="flex items-baseline justify-between gap-3 mb-6">
                    <h3 className="serif-display text-2xl md:text-3xl leading-tight">
                      {t.name}
                    </h3>
                    {t.featured && (
                      <span
                        className="text-[9px] tracking-[0.22em] uppercase font-sans font-medium px-2.5 py-1"
                        style={{
                          backgroundColor: "#E8E2D5",
                          color: "#1A1A1A",
                          border: "0.5px solid rgba(26,26,26,0.45)",
                        }}
                      >
                        Featured
                      </span>
                    )}
                  </div>
                  <p
                    className="eyebrow mb-6"
                    style={{ color: "rgba(26,26,26,0.55)" }}
                  >
                    {t.tag}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {t.deliverables.map((d) => (
                      <li
                        key={d}
                        className="flex items-baseline gap-3 text-[0.92rem] leading-relaxed"
                      >
                        <span className="serif-italic text-lg leading-none">—</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  <div
                    className="mt-auto pt-6"
                    style={{ borderTop: "0.5px solid rgba(26,26,26,0.3)" }}
                  >
                    <p
                      className="eyebrow mb-2"
                      style={{ color: "rgba(26,26,26,0.55)" }}
                    >
                      Best for
                    </p>
                    <p className="text-[0.92rem] leading-relaxed serif-italic">
                      {t.bestFor}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            {v.note && (
              <p
                className="mt-8 text-[0.88rem] leading-relaxed max-w-[68ch] serif-italic"
                style={{ color: "rgba(26,26,26,0.65)" }}
              >
                {v.note}
              </p>
            )}
          </div>
        ))}
      </section>

      {/* ADDITIONAL SERVICES */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28"
        style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
        data-testid="additional-services"
      >
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4">
            <p
              className="text-[11px] font-sans font-medium uppercase tracking-[0.22em]"
              style={{ color: "rgba(26,26,26,0.7)" }}
            >
              Also available — additional services
            </p>
          </div>
          <p
            className="md:col-span-7 md:col-start-6 text-[0.95rem] md:text-base leading-relaxed"
            style={{ color: "rgba(26,26,26,0.78)" }}
          >
            One-off engagements and specialist support — usually added to a larger project,
            occasionally booked on their own.
          </p>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {additional.map(([t, c], i) => (
            <li
              key={t}
              data-testid={`additional-${i + 1}`}
              className="p-7 md:p-8"
              style={{
                border: "0.5px solid rgba(26,26,26,0.35)",
                marginLeft: i % 4 !== 0 ? "-0.5px" : "0",
                marginTop: i >= 4 ? "-0.5px" : "0",
              }}
            >
              <h3 className="serif-display text-xl md:text-2xl leading-tight mb-3">
                {t}
              </h3>
              <p
                className="text-[0.9rem] leading-relaxed"
                style={{ color: "rgba(26,26,26,0.7)" }}
              >
                {c}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {/* FOR AGENCIES */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28"
        style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
        data-testid="agency-section"
      >
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p
              className="text-[11px] font-sans font-medium uppercase tracking-[0.22em] mb-4"
              style={{ color: "rgba(26,26,26,0.55)" }}
            >
              For agencies
            </p>
            <h2 className="serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.02]">
              Strategic partnership &amp; <span className="serif-italic">whitelabel.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-base md:text-lg leading-relaxed max-w-[60ch]">
              I work with agencies as a senior strategic resource — embedded on client accounts
              or brought in for specific brand and campaign briefs. All work is delivered under
              your brand. NDA standard.
            </p>
            <ul className="mt-8 flex flex-wrap gap-3">
              {["Whitelabel delivery", "Embedded senior resource", "Campaign leadership"].map(
                (p) => (
                  <li
                    key={p}
                    className="text-[0.78rem] tracking-[0.14em] uppercase font-sans px-4 py-2.5"
                    style={{ border: "0.5px solid #1A1A1A" }}
                  >
                    {p}
                  </li>
                )
              )}
            </ul>
            <div className="mt-10">
              <Link
                to="/contact"
                data-testid="agency-cta"
                className="font-serif text-xl md:text-2xl editorial-link"
              >
                <span className="serif-italic">Partner with the studio</span> →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
